import {
  GetAllExamEventsByExamCenterCodeDocument,
  GetExamEventsByDateRangeDocument,
  useCreateExamEventsMutation,
  useGetAllExamCenterCoursesQuery,
} from '@exam-center/graphql';
import { useNavigate, useParams } from '@exam-center/router';
import { Button, ConfirmationModal, Flex, Text } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo, useState } from 'react';

import {
  RecurringExamEventCreationAllFailedModal,
  RecurringExamEventCreationLoadingModal,
  RecurringExamEventCreationPartialSuccessModal,
  RecurringExamEventReviewInfo,
} from '../../components';
import {
  type RecurringExamEventCreationFormType,
  useRecurringExamEventCreation,
  useRecurringExamEventCreationForm,
} from '../../providers';
import { mapRecurringExamEventCreationFormToCreateExamEventsInput } from '../recurring-exam-event-creation-logic/mapper';
import { RecurringExamEventCreationReviewList } from '../recurring-exam-event-creation-review-list';

import { mapperExamEventToReviewInfo } from './utils';

export default function RecurringExamEventCreationReviewDetails() {
  const { examEvent, building, validateExamEventsData } = useRecurringExamEventCreation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const formContext = useRecurringExamEventCreationForm();
  const [isModalOpen, setIsModalHandler] = useDisclosure(false);
  const [isResultModalOpen, setIsResultModalHandler] = useDisclosure(false);
  // Outlives the result modal: closing it keeps the last outcome, and a retry reads its count.
  const [failedResult, setFailedResult] = useState<{
    successCount: number;
    failedRequestIds: string[];
  } | null>(null);
  const { examCenterCode, year, month } = useParams(
    '/:examCenterCode/exam-events/years/:year/months/:month/recurring-create/review',
  );

  const reviewInfo = mapperExamEventToReviewInfo(
    validateExamEventsData.map((data) => data.isValid),
    building ?? [],
    examEvent,
  );

  // Every submit — first attempt or retry — sends all creatable rounds. The API keys on requestId
  // and reports an already-created round as a success, so there is nothing to filter client-side.
  const creatableRequestIds = validateExamEventsData
    .filter((data) => data.isValid)
    .map((data) => data.requestId);

  const [createExamEvents, { loading }] = useCreateExamEventsMutation({
    onCompleted: (data) => {
      const { examEvents, failedExamEvents } = data.ecCreateExamEvents;
      if (failedExamEvents.length > 0) {
        setFailedResult({
          successCount: examEvents.length,
          failedRequestIds: failedExamEvents.map((failedExamEvent) => failedExamEvent.requestId),
        });
        setIsResultModalHandler.open();
        return;
      }
      showSnackbar({
        message: `สร้างรอบสอบสำเร็จ ${examEvents.length} รอบ`,
        variant: 'success',
      });
      navigate('/:examCenterCode/exam-events/years/:year/months/:month', {
        params: { examCenterCode, year, month },
      });
    },
    onError: () => {
      // A transport failure tells us nothing about what committed, so report every round as failed.
      // A retry cannot duplicate a round that did commit.
      // it should use previous result if it exist since next exam event guarantee to increase or equal to previous create exam event count. So if previous result exist, we can use it to show the result modal instead of showing all failed exam event.
      setFailedResult((previousFailedResult) => ({
        successCount: previousFailedResult?.successCount ?? 0,
        failedRequestIds: previousFailedResult?.failedRequestIds ?? creatableRequestIds,
      }));
      setIsResultModalHandler.open();
    },
    awaitRefetchQueries: true,
    refetchQueries: (result) =>
      result.data?.ecCreateExamEvents.failedExamEvents.length === 0
        ? [GetAllExamEventsByExamCenterCodeDocument, GetExamEventsByDateRangeDocument]
        : [],
  });

  const { data: examCenterCoursesData } = useGetAllExamCenterCoursesQuery({
    variables: {
      getAllExamCenterCoursesInput: {
        examCenterCode,
        sessionCount: 1,
      },
    },
    fetchPolicy: 'network-only',
  });

  const examCoursesOptions = useMemo(() => {
    return (
      examCenterCoursesData?.ecGetAllExamCenterCourses.examCenterCourses.map((course) => ({
        label: course.examCourse.name,
        value: course.examCourse.id,
      })) ?? []
    );
  }, [examCenterCoursesData]);

  useEffect(() => {
    if (!examEvent) {
      navigate('/:examCenterCode/exam-events/years/:year/months/:month/recurring-create', {
        params: { examCenterCode, year, month },
      });
    }
  }, [examEvent]);

  const onSubmit = async (data: RecurringExamEventCreationFormType) => {
    setIsModalHandler.close();
    setIsResultModalHandler.close();

    await createExamEvents({
      variables: {
        createExamEventsInput: mapRecurringExamEventCreationFormToCreateExamEventsInput(
          data,
          validateExamEventsData,
          examCenterCode,
          examCoursesOptions,
        ),
      },
    });
  };

  const failedDates = (failedResult?.failedRequestIds ?? [])
    .map(
      (requestId) => validateExamEventsData.find((e) => e.requestId === requestId)?.examStartedAt,
    )
    .filter((date) => date != null);

  return (
    <>
      <RecurringExamEventReviewInfo {...reviewInfo} />
      <RecurringExamEventCreationReviewList />
      <Flex
        sx={(theme) => ({
          justifyContent: 'flex-end',
          gap: theme.spacing(3),
        })}
      >
        <Button
          variant='outlined'
          size='large'
          onClick={() =>
            navigate('/:examCenterCode/exam-events/years/:year/months/:month/recurring-create', {
              params: { examCenterCode, year, month },
            })
          }
          color='secondary'
        >
          กลับไปแก้ไข
        </Button>
        <Button
          variant='contained'
          size='large'
          onClick={() => setIsModalHandler.open()}
          disabled={reviewInfo.summary.creatableEvents === 0}
        >
          ยืนยันสร้าง ({failedResult?.failedRequestIds.length ?? reviewInfo.summary.creatableEvents}
          ) รอบ
        </Button>
      </Flex>
      <ConfirmationModal
        title='ยืนยันการสร้างรอบสอบ?'
        content={
          <Text>
            {/* คุณกำลังจะสร้างรอบสอบทั้งหมด {reviewInfo.summary.creatableEvents} รอบ */}
            คุณกำลังจะสร้างรอบสอบทั้งหมด{' '}
            {failedResult?.failedRequestIds.length ?? reviewInfo.summary.creatableEvents} รอบ
            <br />
            เมื่อยืนยันแล้ว ระบบจะบันทึกรอบสอบตามข้อมูลที่คุณตั้งค่าไว้
          </Text>
        }
        acceptText='ยืนยันสร้าง'
        discardText='ยกเลิก'
        open={isModalOpen}
        onClose={setIsModalHandler.close}
        onDiscard={setIsModalHandler.close}
        onAccept={formContext.handleSubmit(onSubmit)}
      />
      <RecurringExamEventCreationLoadingModal
        open={loading}
        totalCount={failedResult?.failedRequestIds.length ?? creatableRequestIds.length}
      />
      {failedResult?.successCount === 0 ? (
        <RecurringExamEventCreationAllFailedModal
          open={isResultModalOpen}
          failedCount={failedResult.failedRequestIds.length}
          onRetry={formContext.handleSubmit(onSubmit)}
          onClose={setIsResultModalHandler.close}
        />
      ) : (
        <RecurringExamEventCreationPartialSuccessModal
          open={isResultModalOpen}
          successCount={failedResult?.successCount ?? 0}
          failedDates={failedDates}
          onRetry={formContext.handleSubmit(onSubmit)}
          onClose={setIsResultModalHandler.close}
        />
      )}
    </>
  );
}
